/**
 * The sample charts the theme screen draws.
 *
 * ─── WHY A FIXTURE AND NOT THE PROJECT'S OWN CHARTS ───────────────────────────────────────────────
 * A preview built from real pages has a data dependency, so it fails when a query fails — and it has
 * holes wherever the project happens to own no donut. This has neither: it needs no client, no
 * network and no selected project, it is identical for every builder, and it is therefore
 * snapshot-testable.
 *
 * ⚠ STRESS-SHAPED, NOT TIDY, and that is the whole point. A palette that looks good on four neat
 * categories falls apart on nine real ones; a density that reads well on short labels clips on long
 * ones. Judging a theme against flattering data is how a builder ships a theme that fails on their
 * own dashboard. So the fixture deliberately contains what real data contains:
 *
 *   · nine categories — one past the point where distinguishable hues run out, so folding shows
 *   · labels long enough to force the orientation flip
 *   · a series an order of magnitude larger than its neighbour, so a shared axis is visible
 *   · a null, so gaps draw as gaps rather than as zeros
 *   · a negative, so the zero-baseline rule is visible
 *   · a donut sitting exactly on the fold-to-"Other" boundary
 *   · a MULTI-SERIES line, so the legend, per-series colour and a softened tail stroke are visible
 *
 * `tools/prism-style-tests.mjs` asserts each of those properties, so the fixture cannot quietly be
 * tidied up later.
 *
 * ⚠ THERE IS NO `stat` ENTRY. A single number is a CARD, not a chart — Blueprint emits
 * `<Card><Label>…</Label><Stat>…</Stat></Card>` for that — so `stat` was removed from PrismForm on
 * 2026-08-31 and there is nothing here to preview.
 *
 * Labels are plausible business strings rather than 'a'/'b'/'c': the preview is judged by eye, and
 * placeholder text hides exactly the width problems this exists to reveal.
 */
import type { PrismForm, PrismSpec } from './prism-render.util';

export interface PreviewChart {
  form: PrismForm;
  title: string;
  spec: PrismSpec;
  /**
   * What the brief's `measure` would have been — `count` | `sum` | `avg` | `min` | `max`.
   *
   * ⚠ WITHOUT THIS THE PREVIEW LIES ABOUT STACKING. The additivity gate refuses `stacked` on a
   * non-additive measure, and it reads that measure off the DATA passed to the resolver. A fixture
   * with no measure is treated as unknown, which ALLOWS — so a builder could choose Stacked here,
   * watch every preview stack happily, and then get Grouped on a real chart whose measure is `avg`.
   * A preview that cannot show a refusal is not a preview of the thing that refuses.
   */
  measure?: string;
}

const MONTHS = ['2026-01-01', '2026-02-01', '2026-03-01', '2026-04-01', '2026-05-01', '2026-06-01']
  .map(d => `${d}T00:00:00.000Z`);

/** Nine dealers — one past the six-hue ceiling, so the fold is visible rather than theoretical. */
const DEALERS = ['Acme Aqua', 'Bellweather', 'Cortez & Co', 'Danforth', 'Eastgate',
                 'Fairview', 'Granite Bay', 'Harlow', 'Ironside'];

export const PREVIEW_FIXTURE: PreviewChart[] = [
  { form: 'sparkline', title: 'Six-month trend', measure: 'sum',
    spec: { labels: MONTHS, series: [{ name: 'Revenue', values: [42, 47, 44, 51, 58, 61] }],
            meta: { unit: 'count', ordered: true, order: 'chronological' } } },

  // NINE categories: past the hue supply, so the never-reuse-a-colour rule has to act.
  { form: 'bar', title: 'Orders by dealer', measure: 'count',
    spec: { labels: DEALERS, series: [{ name: 'Orders', values: [312, 287, 241, 198, 176, 154, 131, 96, 74] }],
            meta: { unit: 'count', order: 'value-desc' } } },

  // LONG LABELS — the orientation fit has something to actually decide.
  { form: 'hbar', title: 'Revenue by territory', measure: 'sum',
    spec: { labels: ['North East Coastal Region', 'Western Delta Districts',
                     'Southern Highlands Belt', 'Central River Basin'],
            series: [{ name: 'Revenue', values: [184000, 152000, 121000, 98000] }],
            meta: { unit: '₹' } } },

  // A NULL: a month with no reading draws as a gap, never as a zero.
  { form: 'line', title: 'Average weight', measure: 'avg',
    spec: { labels: MONTHS,
            series: [{ name: 'Grams', values: [180, 214, null as any, 268, 291, 317] }],
            meta: { unit: 'count', ordered: true, order: 'chronological' } } },

  // ⚠ A MULTI-SERIES LINE, because the single-series one above cannot show any of what changes when
  // there are several: the legend appearing, colours varying by SERIES rather than by category, and
  // the soft tail landing on a stroke instead of a fill. Multi-line always worked and was never once
  // looked at, because nothing in the preview had more than one line.
  { form: 'line', title: 'Weight by pond', measure: 'avg',
    spec: { labels: MONTHS,
            series: [{ name: 'Pond A', values: [180, 214, 238, 268, 291, 317] },
                     { name: 'Pond B', values: [162, 191, 205, 232, 254, 271] },
                     { name: 'Pond C', values: [148, 158, 176, 190, 212, 236] },
                     { name: 'Pond D', values: [131, 140, 152, 161, 178, 195] }],
            meta: { unit: 'count', ordered: true, order: 'chronological' } } },

  { form: 'area', title: 'Cumulative harvest', measure: 'sum',
    spec: { labels: MONTHS, series: [{ name: 'Tonnes', values: [12, 26, 41, 59, 82, 108] }],
            meta: { unit: 'count', ordered: true, order: 'chronological' } } },

  // SIX slices — exactly ON the default ceiling, not past it.
  //
  // ⚠ Seven was the first choice, to show the fold. It showed something else: `fitForm` demotes a
  // donut past the ceiling to a BAR, so the card titled "donut" drew a bar chart and the preview
  // never showed a donut at all. Sitting on the boundary keeps the form visible AND makes the show
  // count visibly do something — drop it to 5 and the tail folds into Other in front of you.
  { form: 'donut', title: 'Order status', measure: 'count',
    spec: { labels: ['Confirmed', 'Packed', 'Shipped', 'Delivered', 'Returned', 'Cancelled'],
            series: [{ name: 'Orders', values: [420, 260, 190, 140, 62, 38] }],
            meta: { unit: 'count' } } },

  { form: 'stacked', title: 'Volume by channel', measure: 'count',
    spec: { labels: MONTHS,
            series: [{ name: 'Direct', values: [30, 34, 31, 38, 41, 44] },
                     { name: 'Dealer', values: [22, 25, 28, 26, 30, 33] },
                     { name: 'Online', values: [8, 11, 14, 17, 19, 24] }],
            meta: { unit: 'count', ordered: true } } },

  { form: 'grouped', title: 'Target against actual', measure: 'sum',
    spec: { labels: ['Q1', 'Q2', 'Q3', 'Q4'],
            series: [{ name: 'Target', values: [120, 140, 160, 180] },
                     { name: 'Actual', values: [112, 151, 143, 197] }],
            meta: { unit: 'count' } } },

  { form: 'gauge', title: 'Target attainment', measure: 'avg',
    spec: { labels: ['Attained'], series: [{ name: 'Percent', values: [68] }],
            meta: { unit: 'percent' } } },

  // ONE SERIES AN ORDER OF MAGNITUDE ABOVE THE OTHERS. It lives here rather than on the combo
  // because a 100% stack NORMALISES — so a dominant series is legible as a share instead of
  // flattening its neighbours into the axis, which is exactly the case this form exists for.
  { form: 'stacked100', title: 'Channel mix', measure: 'count',
    spec: { labels: ['Q1', 'Q2', 'Q3', 'Q4'],
            series: [{ name: 'Direct', values: [300, 340, 310, 380] },
                     { name: 'Dealer', values: [22, 25, 28, 26] },
                     { name: 'Online', values: [8, 11, 14, 17] }],
            meta: { unit: 'percent' } } },

  { form: 'hstacked', title: 'Stock by grade', measure: 'count',
    spec: { labels: ['Premium Export Grade', 'Domestic Retail Grade', 'Processing Grade'],
            series: [{ name: 'In tank', values: [4200, 3100, 1900] },
                     { name: 'Harvested', values: [1800, 2400, 3300] }],
            meta: { unit: 'count' } } },

  // A combo shares ONE axis on purpose — a second axis lets two unrelated scales be aligned to imply
  // a relationship that is not there. That compression has to be VISIBLE, not fatal: at the 26× ratio
  // this first carried, the line collapsed onto the baseline and the form looked broken rather than
  // compromised. ~4× shows the squeeze and still draws a line you can read.
  { form: 'combo', title: 'Volume and conversion', measure: 'count',
    spec: { labels: MONTHS,
            series: [{ name: 'Orders', values: [820, 910, 870, 1040, 1180, 1260] },
                     { name: 'Conversion', values: [210, 245, 232, 268, 301, 324] }],
            meta: { unit: 'count', ordered: true } } },

  // A NEGATIVE: the zero baseline has to be visible rather than assumed.
  { form: 'scatter', title: 'Density against yield', measure: 'avg',
    spec: { labels: ['12', '18', '24', '30', '36', '42', '48'],
            series: [{ name: 'Yield delta', values: [-14, -6, 3, 11, 18, 9, -4] }],
            meta: { unit: 'count' } } },
];
